import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { type AuthRepository } from './auth.repository';
import { AuthContext, AuthRepositoryContext, type AuthState } from './auth-context';

interface AuthProviderProps {
  repository: AuthRepository;
  children: ReactNode;
}

/**
 * Pumps Supabase session events into AuthContext.
 * - On mount: hydrate from cached session (Supabase persists in localStorage).
 * - Then subscribe to onAuthStateChange for live updates.
 */
export function AuthProvider({ repository, children }: AuthProviderProps): React.JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading', session: null });

  useEffect(() => {
    let cancelled = false;
    void repository.getCurrentSession().then((session) => {
      if (cancelled) return;
      setState(
        session
          ? { status: 'authenticated', session }
          : { status: 'unauthenticated', session: null },
      );
    });
    const unsubscribe = repository.onSessionChange((session) => {
      setState(
        session
          ? { status: 'authenticated', session }
          : { status: 'unauthenticated', session: null },
      );
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [repository]);

  const repoValue = useMemo(() => repository, [repository]);

  return (
    <AuthRepositoryContext.Provider value={repoValue}>
      <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
    </AuthRepositoryContext.Provider>
  );
}
